import React, { useState, useEffect } from 'react';
import { format, subDays, addDays, isSameDay } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useDate } from '../pages/Layout';
import GlassCard from '../../../shared/components/card/GlassCard';
import DotGrid from './DotGrid';

const InventarioResumen = () => {
  const { getInventario } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentDate, setIsLoading } = useDate();

  useEffect(() => {
    const fetchInventario = async () => {
      try {
        setLoading(true);
        setError(null);
        const responsePetition = await getInventario();
        console.log("inventario ",responsePetition)
        const response = responsePetition.data;
        
        // Normalizar a array (soporta tanto array como objeto único)
        const inventarioArray = Array.isArray(response) ? response : 
                               (response ? [response] : []);
        setData(inventarioArray);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchInventario();
  }, [getInventario,currentDate,setIsLoading]);

  // Estado de carga - usa las clases del CSS compartido
    if (loading) {
        return (
            <div className="inventario-loading">
            <GlassCard maxWidth="100%">
                <div className="inventario-loading__content">
                <DotGrid />
                </div>
            </GlassCard>
            </div>
        );
    }

    // Estado de error
    if (error) {
        return (
            <GlassCard maxWidth="100%">
                <div className="productos-error">
                    <div className="error-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </div>
                    <div className="error-text">Error al cargar el inventario</div>
                    <button className="retry-btn" onClick={() => window.location.reload()}>
                        Reintentar
                    </button>
                </div>
            </GlassCard>
        );
    }

    // Estado vacío
    if (!data || data.length === 0) {
        return (
            <GlassCard maxWidth="100%">
                <div className="productos-empty">
                    <div className="productos-empty-icon">📦</div>
                    <div>No hay datos de inventario</div>
                    <div className="empty-subtext">No se encontraron registros para la fecha seleccionada</div>
                </div>
            </GlassCard>
        );
    }

  return (
    <div className="inventario-container">
        {data.map((inventario, index) => (
            <InventarioCard 
            key={index} 
            inventario={inventario} 
            index={index}
            />
        ))}
    </div>
  );

};

// Sub-componente individual para cada inventario (mantiene tus estilos exactos)
const InventarioCard = ({ inventario, index }) => {
  const {
    unidades = 0,
    referencias = 0,
    valoracionPVP = 0,
    valoracionCoste = 0,
    valoracionCostePropio = 0,
    nombre, // Por si el objeto trae nombre/identificador
    almacen,
    fecha
  } = inventario;

  // Color derivado (añadido index para variación entre tarjetas)
  const getInventarioColor = () => {
    const seed = Math.floor(valoracionPVP / 1000) + unidades + (index * 50);
    const h = Math.abs(seed * 137) % 360;
    return `hsl(${h}, 65%, 45%)`;
  };

  const themeColor = getInventarioColor();

  const formatCurrency = (value) => {
    return Number(value).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatNumber = (value) => {
    return Number(value).toLocaleString('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  // Cálculos
  const margenPotencial = valoracionPVP - valoracionCoste;
  const margenPercent = valoracionPVP > 0 ? ((margenPotencial / valoracionPVP) * 100) : 0;
  const unidadesPorReferencia = referencias > 0 ? (unidades / referencias) : 0;

  // Título dinámico
  const titulo = nombre || almacen || `Inventario Actual`;

  return (
    <GlassCard maxWidth='100%'>
      <div className="inventario-resumen">
        {/* Header - idéntico al original */}
        <div className="inventario-header" style={{ borderBottomColor: themeColor }}>
          <div className="inventario-titulo">
            <div className="inventario-icono-principal" style={{ background: themeColor }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div>
              <h3>{titulo}</h3>
              <span className="inventario-subtitulo">
                {formatNumber(referencias)} referencias · Stock total
              </span>
            </div>
          </div>
          <div className="inventario-fecha">
            {fecha ? new Date(fecha).toLocaleDateString('es-ES') : 
             new Date().toLocaleDateString('es-ES')}
          </div>
        </div>

        {/* Métricas - idéntico al original */}
        <div className="inventario-metricas">
          <div className="inventario-metrica stock">
            <div className="metrica-header">
              <span className="metrica-label">Unidades Totales</span>
              <div className="metrica-icono" style={{ color: themeColor }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
              </div>
            </div>
            <span className="metrica-valor">{formatNumber(unidades)}</span>
          </div>

          <div className="inventario-metrica pvp">
            <div className="metrica-header">
              <span className="metrica-label">Valor PVP</span>
              <div className="metrica-icono" style={{ color: '#0ea5e9' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </div>
            </div>
            <span className="metrica-valor currency">{formatCurrency(valoracionPVP)} €</span>
            <span className="metrica-meta">Precio venta</span>
          </div>

          <div className="inventario-metrica coste">
            <div className="metrica-header">
              <span className="metrica-label">Valor Coste</span>
              <div className="metrica-icono" style={{ color: '#f59e0b' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </div>
            </div>
            <span className="metrica-valor currency">{formatCurrency(valoracionCoste)} €</span>
            <span className="metrica-meta">Coste adquisición</span>
          </div>

          <div className="inventario-metrica coste-propio">
            <div className="metrica-header">
              <span className="metrica-label">Coste Propio</span>
              <div className="metrica-icono" style={{ color: '#8b5cf6' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
            </div>
            <span className="metrica-valor currency">{formatCurrency(valoracionCostePropio)} €</span>
            <span className="metrica-meta">Coste interno</span>
          </div>
        </div>

        {/* Barra de análisis - idéntica al original */}
        <div className="inventario-analisis">
          <div className="analisis-info">
            <span className="analisis-label">Margen Potencial</span>
            <span className={`analisis-valor ${margenPotencial >= 0 ? 'positivo' : 'negativo'}`}>
              {formatCurrency(margenPotencial)} € ({margenPercent.toFixed(1)}%)
            </span>
          </div>
          <div className="analisis-barra">
            <div className="barra-fondo">
              <div 
                className="barra-progreso" 
                style={{ 
                  width: `${Math.min(Math.abs(margenPercent), 100)}%`,
                  background: margenPotencial >= 0 ? themeColor : '#ef4444',
                  marginLeft: margenPotencial < 0 ? 'auto' : '0'
                }}
              ></div>
            </div>
            <div className="barra-marcadores">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

export default InventarioResumen;